import {
  createAuthorizedHeaders,
  fetchAwsApi,
  normalizeTuxedoCategoryInJsonBody,
  sanitizeApiBaseUrl,
} from './security';
import { getAccountWriteQueue } from './queuedAccountWrites';

const legacyRouteToNetlifyFunctionName = new Map([
  ['add_account', 'add-account'],
  ['add_address', 'add-address'],
  ['add_item', 'add-item'],
  ['add_items', 'add-item'],
  ['delete_item', 'delete-item'],
  ['delete_user_ai_photo', 'delete-user-ai-photo'],
  ['delete_user_photo', 'delete-user-photo'],
  ['get_item_photo', 'get-item-photo'],
  ['get_fabric_swatch', 'get-fabric-swatch'],
  ['manage_user_ai_data', 'manage-user-ai-data'],
  ['try_on_add_item', 'try-on-add-item'],
  ['upload_item_photos', 'upload-item-photos'],
  ['manage_messages', 'manage-messages']
]);

function normalizeApiPath(path: string) {
  return path.startsWith('/') ? path : `/${path}`;
}

function dedupeEndpoints(endpointCandidates: string[]) {
  return Array.from(new Set(endpointCandidates.filter(Boolean)));
}

function getConfiguredFunctionsApiBaseUrl() {
  return sanitizeApiBaseUrl(import.meta.env.VITE_FUNCTIONS_API_URL);
}

function getLegacyAwsApiBaseUrl() {
  return sanitizeApiBaseUrl(import.meta.env.VITE_AWS_API_URL);
}

function getConfiguredCloudFrontBaseUrl() {
  return typeof __CLOUDFRONT_URL__ === 'string'
    ? sanitizeApiBaseUrl(__CLOUDFRONT_URL__)
    : '';
}

function encodeObjectKeyPath(objectKey: string) {
  const normalizedObjectKey = objectKey.trim().replace(/^\/+/, '');
  if (!normalizedObjectKey) {
    return '';
  }

  const pathSegments = normalizedObjectKey.split('/');
  if (pathSegments.some((segment) => segment === '.' || segment === '..')) {
    return '';
  }

  return pathSegments.map((segment) => encodeURIComponent(segment)).join('/');
}

export function buildCloudFrontObjectUrl(baseUrl: string, objectKey: string) {
  const normalizedBaseUrl = sanitizeApiBaseUrl(baseUrl);
  const encodedObjectKey = encodeObjectKeyPath(objectKey);

  return normalizedBaseUrl && encodedObjectKey
    ? `${normalizedBaseUrl}/${encodedObjectKey}`
    : '';
}

export function extractCloudFrontObjectKey(imageUrl: string) {
  const cloudFrontBaseUrl = getConfiguredCloudFrontBaseUrl();
  if (!cloudFrontBaseUrl) {
    return '';
  }

  try {
    const baseUrl = new URL(`${cloudFrontBaseUrl}/`);
    const parsedImageUrl = new URL(imageUrl);
    const basePath = baseUrl.pathname.replace(/\/+$/, '');
    const imagePath = parsedImageUrl.pathname;
    const isWithinBasePath = !basePath
      || imagePath === basePath
      || imagePath.startsWith(`${basePath}/`);

    if (parsedImageUrl.origin !== baseUrl.origin || !isWithinBasePath) {
      return '';
    }

    const encodedObjectKey = imagePath.slice(basePath.length).replace(/^\/+/, '');
    const objectKeySegments = encodedObjectKey.split('/').map((segment) => decodeURIComponent(segment));
    if (
      objectKeySegments.length === 0
      || objectKeySegments.some((segment) => !segment || segment === '.' || segment === '..')
    ) {
      return '';
    }

    return objectKeySegments.join('/');
  } catch {
    return '';
  }
}

function getNetlifyFunctionName(path: string) {
  const normalizedPath = normalizeApiPath(path);

  if (!normalizedPath.startsWith('/api/')) {
    return '';
  }

  const apiRoute = normalizedPath.slice('/api/'.length).split('?')[0];
  if (!apiRoute) {
    return '';
  }

  return legacyRouteToNetlifyFunctionName.get(apiRoute) ?? apiRoute;
}

function buildNetlifyFunctionsEndpoint(path: string) {
  const normalizedPath = normalizeApiPath(path);
  const functionName = getNetlifyFunctionName(path);
  if (!functionName) {
    return '';
  }

  const queryIndex = normalizedPath.indexOf('?');
  const search = queryIndex >= 0 ? normalizedPath.slice(queryIndex) : '';
  return `/.netlify/functions/${functionName}${search}`;
}

export function buildFunctionsApiEndpoint(path: string) {
  return buildFunctionsApiEndpointCandidates(path)[0] ?? normalizeApiPath(path);
}

export function buildItemPhotoEndpoint(objectKey: string) {
  const cloudFrontBaseUrl = getConfiguredCloudFrontBaseUrl();
  const cloudFrontUrl = buildCloudFrontObjectUrl(cloudFrontBaseUrl, objectKey);
  if (cloudFrontUrl) {
    return cloudFrontUrl;
  }

  const encodedObjectKey = encodeURIComponent(objectKey);
  const itemPhotoEndpointBase = buildFunctionsApiEndpoint('/api/get-item-photo');
  return `${itemPhotoEndpointBase}?objectKey=${encodedObjectKey}`;
}

export function buildItemPhotoProxyEndpointCandidates(objectKey: string) {
  const normalizedObjectKey = objectKey.trim().replace(/^\/+/, '');
  if (!normalizedObjectKey) {
    return [];
  }

  return buildFunctionsApiEndpointCandidates(
    `/api/get-item-photo?objectKey=${encodeURIComponent(normalizedObjectKey)}`
  );
}

export function buildFabricSwatchEndpoint(objectKey: string) {
  const normalizedObjectKey = objectKey.trim().replace(/^\/+/, '');
  if (normalizedObjectKey && !normalizedObjectKey.startsWith('swatches/')) {
    return buildItemPhotoEndpoint(objectKey);
  }

  const encodedObjectKey = encodeURIComponent(objectKey);
  const endpointBase = buildFunctionsApiEndpoint('/api/get-fabric-swatch');
  return `${endpointBase}?objectKey=${encodedObjectKey}`;
}

export function buildTryOnTaskImageEndpoint(taskId: string, provider?: string) {
  const encodedTaskId = encodeURIComponent(taskId);
  const tryOnTaskImageEndpointBase = buildFunctionsApiEndpoint('/api/get-tryon-result');
  const providerSearch = provider ? `&provider=${encodeURIComponent(provider)}` : '';
  return `${tryOnTaskImageEndpointBase}?taskId=${encodedTaskId}${providerSearch}`;
}

export function buildFunctionsApiEndpointCandidates(path: string) {
  const normalizedPath = normalizeApiPath(path);
  const configuredFunctionsApiBaseUrl = getConfiguredFunctionsApiBaseUrl();
  const legacyAwsApiBaseUrl = getLegacyAwsApiBaseUrl();
  const netlifyFunctionsEndpoint = buildNetlifyFunctionsEndpoint(normalizedPath);

  return dedupeEndpoints([
    configuredFunctionsApiBaseUrl && netlifyFunctionsEndpoint
      ? `${configuredFunctionsApiBaseUrl}${netlifyFunctionsEndpoint}`
      : '',
    netlifyFunctionsEndpoint,
    configuredFunctionsApiBaseUrl ? `${configuredFunctionsApiBaseUrl}${normalizedPath}` : '',
    normalizedPath,
    legacyAwsApiBaseUrl ? `${legacyAwsApiBaseUrl}${normalizedPath}` : '',
  ]);
}

export function buildNetlifyOnlyApiEndpointCandidates(path: string) {
  const legacyAwsApiBaseUrl = getLegacyAwsApiBaseUrl();

  return buildFunctionsApiEndpointCandidates(path).filter((endpoint) => (
    !legacyAwsApiBaseUrl
    || !endpoint.startsWith(legacyAwsApiBaseUrl)
  ));
}

export function buildLegacyAwsApiEndpointCandidates(path: string) {
  const normalizedPath = normalizeApiPath(path);
  const legacyAwsApiBaseUrl = getLegacyAwsApiBaseUrl();

  return dedupeEndpoints([
    legacyAwsApiBaseUrl ? `${legacyAwsApiBaseUrl}${normalizedPath}` : '',
  ]);
}

export function buildDirectNetlifyFunctionEndpointCandidates(path: string) {
  const netlifyFunctionsEndpoint = buildNetlifyFunctionsEndpoint(path);
  return dedupeEndpoints([netlifyFunctionsEndpoint]);
}

export function buildAddItemLookupEndpointCandidates(queryString: string) {
  const normalizedQueryString = queryString
    ? queryString.startsWith('?')
      ? queryString
      : `?${queryString}`
    : '';
  const searchParams = new URLSearchParams(normalizedQueryString.replace(/^\?/, ''));
  const singleItemId = searchParams.get('item_id') || searchParams.get('id');
  const queryStrings = singleItemId
    ? [
        `?item_id=${encodeURIComponent(singleItemId)}`,
        `?id=${encodeURIComponent(singleItemId)}`,
      ]
    : [normalizedQueryString];

  return dedupeEndpoints(
    ['/api/add_items', '/api/add_item', '/api/add-item'].flatMap((path) =>
      queryStrings.flatMap((query) => buildFunctionsApiEndpointCandidates(`${path}${query}`))
    )
  );
}

const STORE_SCOPED_ADD_ITEMS_PATHS = new Set([
  '/api/add_items',
  '/api/add_item',
  '/api/add-item',
  '/.netlify/functions/add-item',
]);

function resolveStoreName() {
  return typeof __STORE_NAME__ === 'string' ? __STORE_NAME__.trim() : '';
}

function getEndpointPathName(endpoint: string) {
  try {
    return new URL(endpoint, 'http://localhost').pathname;
  } catch {
    return '';
  }
}

function appendQueryParameter(url: string, key: string, value: string) {
  const separator = url.includes('?') ? '&' : '?';
  return `${url}${separator}${key}=${encodeURIComponent(value)}`;
}

function injectStoreIntoJsonBody(body: BodyInit | null | undefined, storeName: string) {
  if (typeof body !== 'string' || !body) {
    return body;
  }

  try {
    const parsedBody = JSON.parse(body) as unknown;
    if (parsedBody && typeof parsedBody === 'object' && !Array.isArray(parsedBody)) {
      const bodyRecord = parsedBody as Record<string, unknown>;
      if ('store' in bodyRecord) {
        return body;
      }

      return JSON.stringify({
        ...bodyRecord,
        store: storeName,
      });
    }
  } catch {
    return body;
  }

  return body;
}

function isStoreScopedAddItemsEndpoint(endpoint: string) {
  return STORE_SCOPED_ADD_ITEMS_PATHS.has(getEndpointPathName(endpoint));
}

function createAuthorizedRequestInit(init: RequestInit = {}) {
  return {
    ...init,
    headers: createAuthorizedHeaders(init.headers),
  };
}

function createStoreScopedRequestInit(
  endpointCandidates: string[],
  init: RequestInit
): { endpointCandidates: string[]; init: RequestInit } {
  const storeName = resolveStoreName();
  const hasAddItemsEndpoint = endpointCandidates.some(isStoreScopedAddItemsEndpoint);
  if (!hasAddItemsEndpoint) {
    return { endpointCandidates, init };
  }

  const requestMethod = (init.method ?? 'GET').toUpperCase();
  if (requestMethod === 'GET') {
    if (!storeName) {
      return { endpointCandidates, init };
    }

    return {
      endpointCandidates: endpointCandidates.map((endpoint) =>
        isStoreScopedAddItemsEndpoint(endpoint)
          ? appendQueryParameter(endpoint, 'store', storeName)
          : endpoint
      ),
      init,
    };
  }

  if (requestMethod === 'POST' || requestMethod === 'PUT' || requestMethod === 'DELETE') {
    let body = init.body;
    if (requestMethod === 'POST' || requestMethod === 'PUT') {
      body = normalizeTuxedoCategoryInJsonBody(body);
    }
    if (storeName) {
      body = injectStoreIntoJsonBody(body, storeName);
    }

    return {
      endpointCandidates,
      init: {
        ...init,
        body,
      },
    };
  }

  return { endpointCandidates, init };
}

function isRetryableFunctionsApiError(error: unknown) {
  if (!(error instanceof Error)) {
    return false;
  }

  const normalizedMessage = error.message.trim().toLowerCase();
  return (
    error.name === 'TypeError'
    || normalizedMessage.includes('failed to fetch')
    || normalizedMessage.includes('load failed')
    || normalizedMessage.includes('networkerror')
  );
}

function isHtmlResponse(response: Response) {
  return response.headers.get('content-type')?.toLowerCase().includes('text/html') ?? false;
}

function isJsonResponse(response: Response) {
  return response.headers.get('content-type')?.toLowerCase().includes('application/json') ?? false;
}

async function fetchFunctionsApiWithFallback(
  endpointCandidates: string[],
  init: RequestInit = {},
  throwWhenAllNotFound = false,
  notFoundMessage = 'Required function endpoint was not found. Restart the dev server and try again.'
) {
  const { endpointCandidates: storeScopedCandidates, init: storeScopedInit } =
    createStoreScopedRequestInit(endpointCandidates, init);
  const requestInit = createAuthorizedRequestInit(storeScopedInit);
  let lastError: unknown = null;

  for (let index = 0; index < storeScopedCandidates.length; index += 1) {
    const endpoint = storeScopedCandidates[index];
    const isLastEndpoint = index === storeScopedCandidates.length - 1;

    try {
      const response = await fetch(endpoint, requestInit);

      if (isHtmlResponse(response)) {
        if (!isLastEndpoint) {
          continue;
        }

        throw new Error('Required API endpoint returned the frontend app instead of JSON.');
      }

      if (response.status === 404 && !isLastEndpoint) {
        continue;
      }

      if (response.status === 404 && throwWhenAllNotFound && !isJsonResponse(response)) {
        continue;
      }

      return response;
    } catch (error) {
      lastError = error;

      if (isLastEndpoint || !isRetryableFunctionsApiError(error)) {
        throw error;
      }
    }
  }

  if (lastError) {
    throw lastError;
  }

  throw new Error(notFoundMessage);
}

export async function fetchFunctionsApi(path: string, init: RequestInit = {}) {
  if (getAccountWriteQueue(normalizeApiPath(path), init)) return fetchAwsApi(path, init);
  return fetchFunctionsApiWithFallback(buildFunctionsApiEndpointCandidates(path), init);
}

export async function fetchPrimaryFunctionsApi(path: string, init: RequestInit = {}) {
  if (getAccountWriteQueue(normalizeApiPath(path), init)) return fetchAwsApi(path, init);
  const response = await fetch(buildFunctionsApiEndpoint(path), createAuthorizedRequestInit(init));

  if (isHtmlResponse(response)) {
    throw new Error('Required API endpoint returned the frontend app instead of JSON.');
  }

  return response;
}

export async function fetchFirstAvailableFunctionsApi(
  endpointCandidates: string[],
  init: RequestInit = {},
  notFoundMessage = 'Required function endpoint was not found. Restart the dev server and try again.'
) {
  const queuedEndpoint = endpointCandidates.find((endpoint) => getAccountWriteQueue(endpoint, init));
  if (queuedEndpoint) {
    const url = new URL(queuedEndpoint, 'http://localhost');
    return fetchAwsApi(`${url.pathname}${url.search}`, init);
  }
  return fetchFunctionsApiWithFallback(endpointCandidates, init, true, notFoundMessage);
}
